// Client wrappers for the comp-* edge functions. See plan v5.
//
// All wrappers call supabase.functions.invoke (which handles JWT + URL automatically).
// Errors are normalized into a CompensationServiceError so callers can show the message
// directly to admins/LOs.

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { FunctionsHttpError } from '@supabase/supabase-js';

const log = createComponentLogger('CompensationService', 'compensation');

export class CompensationServiceError extends Error {
  status?: number;
  code?: string;
  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'CompensationServiceError';
    this.status = status;
    this.code = code;
  }
}

async function invokeFn<T>(fn: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(fn, { body });
  if (error) {
    let message = error.message ?? `${fn} failed`;
    let status: number | undefined;
    let code: string | undefined;

    if (error instanceof FunctionsHttpError) {
      status = error.context.status;
      try {
        const payload = await error.context.json() as unknown;
        if (payload && typeof payload === 'object') {
          const responseBody = payload as { error?: unknown; message?: unknown; code?: unknown };
          const responseMessage = responseBody.error ?? responseBody.message;
          if (typeof responseMessage === 'string' && responseMessage.trim()) {
            message = responseMessage;
          }
          if (typeof responseBody.code === 'string') {
            code = responseBody.code;
          }
        }
      } catch {
        // The response body may be empty or non-JSON. Preserve the SDK message.
      }
    }

    log.error(`${fn} failed`, {
      operation: fn,
      metadata: { status, code, serverMessage: message }
    }, error);
    throw new CompensationServiceError(message, status, code);
  }
  if (data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)) {
    const errMsg = String((data as { error?: unknown }).error ?? `${fn} returned an error`);
    throw new CompensationServiceError(errMsg);
  }
  return data as T;
}

// ─── comp-set ─────────────────────────────────────────────────────────────

export interface CompSetInput {
  userId: string;
  banked_compensation?: number | null;
  maximum_compensation?: number | null;
  broker_commission_split?: number | null;
  banked_override_bps?: number | null;
  broker_mentor_commission_split?: number | null;
  override_recipient_id?: string | null;
  mentor_id?: string | null;
  mentorship_active?: boolean | null;
  // Required for admin broker-split changes. Uses the Pacific business-date
  // calendar and must be later than today so prior fundings keep their split.
  effective_date?: string;
}

export interface CompSetResult { success: boolean; history_id?: string | null; }

export function compSet(input: CompSetInput): Promise<CompSetResult> {
  return invokeFn<CompSetResult>('comp-set', input);
}

export interface CompSetEditableValues {
  banked_compensation: number | null;
  maximum_compensation: number | null;
  broker_commission_split: number | null;
  banked_override_bps: number | null;
  broker_mentor_commission_split: number | null;
  override_recipient_id: string | null;
  mentor_id: string | null;
}

export type CompSetDirtyFields = Partial<Record<keyof CompSetEditableValues, boolean>>;

/**
 * Build the sparse comp-set contract from React Hook Form's field-level dirty
 * state. Explicit intent avoids resending stale OB-managed values. The RPC also
 * tolerates unchanged full-form fields from older portal builds under a row lock.
 */
export function buildCompSetInputFromDirtyFields(
  userId: string,
  values: CompSetEditableValues,
  dirtyFields: CompSetDirtyFields,
  brokerSplitEffectiveDate: string
): CompSetInput {
  const input: CompSetInput = { userId };

  if (dirtyFields.banked_compensation) {
    input.banked_compensation = values.banked_compensation;
  }
  if (dirtyFields.maximum_compensation) {
    input.maximum_compensation = values.maximum_compensation;
  }
  if (dirtyFields.broker_commission_split) {
    input.broker_commission_split = values.broker_commission_split;
    input.effective_date = brokerSplitEffectiveDate;
  }
  if (dirtyFields.banked_override_bps) {
    input.banked_override_bps = values.banked_override_bps;
  }
  if (dirtyFields.broker_mentor_commission_split) {
    input.broker_mentor_commission_split = values.broker_mentor_commission_split;
  }
  if (dirtyFields.override_recipient_id) {
    input.override_recipient_id = values.override_recipient_id;
  }
  if (dirtyFields.mentor_id) {
    input.mentor_id = values.mentor_id;
    input.mentorship_active = values.mentor_id !== null && values.mentor_id !== '';
  }

  return input;
}

export interface BrokerCommissionSplitResolution {
  historyId: string;
  brokerCommissionSplit: number;
  effectiveDate: string;
}

/** Resolve the split that was effective on a loan's date-only funding date. */
export async function resolveBrokerCommissionSplit(
  userId: string,
  fundingDate: string
): Promise<BrokerCommissionSplitResolution> {
  if (!userId) {
    throw new CompensationServiceError('Loan officer is required to resolve a broker split');
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fundingDate)) {
    throw new CompensationServiceError('A valid funded date is required to resolve a broker split');
  }

  const { data, error } = await supabase
    .from('broker_commission_split_history')
    .select('id, broker_commission_split, effective_date')
    .eq('user_id', userId)
    .lte('effective_date', fundingDate)
    .order('effective_date', { ascending: false })
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    log.error('Failed to resolve broker split', {
      userId,
      metadata: { fundingDate }
    }, error);
    throw new CompensationServiceError(`Failed to resolve broker split: ${error.message}`);
  }
  if (!data) {
    throw new CompensationServiceError(
      `No broker commission split was effective on ${fundingDate}. Verify the funded date or contact an administrator.`
    );
  }
  if (data.broker_commission_split === null || data.broker_commission_split === undefined) {
    throw new CompensationServiceError(
      `No broker commission split is configured for fundings on ${fundingDate}.`
    );
  }

  const brokerCommissionSplit = Number(data.broker_commission_split);
  if (!Number.isFinite(brokerCommissionSplit)) {
    throw new CompensationServiceError('The effective broker commission split is invalid');
  }

  return {
    historyId: data.id,
    brokerCommissionSplit,
    effectiveDate: data.effective_date
  };
}

// ─── comp-request ─────────────────────────────────────────────────────────

export interface CompRequestInput {
  targetUserId: string;
  targetScheduleId: number;
  justification?: string;
}

export interface CompRequestResult {
  success: boolean;
  requestId: string;
  effectiveDate: string;             // YYYY-MM-DD
  // Always 'core' going forward — comp-request hard-codes scope_label='core'
  // under the standard-core-only workflow. 'full_footprint' is no longer
  // produced; tightened from the union to remove dead branching for callers.
  scope: 'core';
  productCategoriesSnapshot: string[];
}

export function compRequest(input: CompRequestInput): Promise<CompRequestResult> {
  return invokeFn<CompRequestResult>('comp-request', input);
}

// ─── comp-approve / comp-reject ───────────────────────────────────────────

export interface CompApproveResult { success: boolean; requestId: string; status: 'approved'; }
export function compApprove(requestId: string): Promise<CompApproveResult> {
  return invokeFn<CompApproveResult>('comp-approve', { requestId });
}

export interface CompRejectResult {
  success: boolean;
  requestId: string;
  status: 'rejected';
  // True when the server detected this was a self-cancel (caller owns both
  // the requester and target sides). Lets callers pick the toast wording.
  cancelledBySelf?: boolean;
}
export function compReject(requestId: string, reason: string): Promise<CompRejectResult> {
  return invokeFn<CompRejectResult>('comp-reject', { requestId, reason });
}

// LO self-cancel of their own scheduled request. Server-side guards ensure
// caller === requester === target, status==='approved', and effective_date
// is still in the future. The sentinel reason is the source of truth the
// label helper uses to render "Cancelled" instead of "Rejected".
export const SELF_CANCEL_REASON = 'Cancelled by requester';
export function cancelMyCompRequest(requestId: string): Promise<CompRejectResult> {
  return invokeFn<CompRejectResult>('comp-reject', { requestId, reason: SELF_CANCEL_REASON });
}

// ─── Status label helper (single source of truth for UI badges) ───────────

export type CompRequestStatus =
  | 'pending'
  | 'approved'
  | 'applied'
  | 'rejected'
  | 'failed'
  | 'cancelled'
  | 'needs_review';

// Returns "YYYY-MM-DD" for today in America/Los_Angeles. Mirrors
// supabase/functions/_shared/comp-utils.ts::todayLaDateString so the
// Scheduled vs Processing boundary is computed against the same wall-clock
// the cron uses (today_LA).
function todayLaDateString(now: Date = new Date()): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return fmt.format(now);
}

export function formatCompRequestStatus(
  status: CompRequestStatus | string,
  effectiveDateIso?: string | null,
  lastFailureReason?: string | null
): string {
  if (status === 'approved') {
    const today = todayLaDateString();
    const effDate = (effectiveDateIso ?? '').slice(0, 10);
    return effDate && effDate > today ? 'Scheduled' : 'Processing';
  }
  if (status === 'applied') {
return 'Active';
}
  if (status === 'pending') {
return 'Pending Approval';
}
  if (status === 'needs_review') {
return 'Needs Review';
}
  if (status === 'failed') {
return 'Failed';
}
  if (status === 'rejected') {
    return lastFailureReason === SELF_CANCEL_REASON ? 'Cancelled' : 'Rejected';
  }
  if (status === 'cancelled') {
return 'Cancelled';
}
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// ─── comp-schedule-set-selectable ─────────────────────────────────────────

export function compScheduleSetSelectable(
  scheduleId: number,
  selectable: boolean
): Promise<{ success: boolean; scheduleId: number; selectable: boolean }> {
  return invokeFn('comp-schedule-set-selectable', { scheduleId, selectable });
}

// ─── ob-comp-sync (plans only) ─────────────────────────────────────────────

export interface ObCompSyncResult {
  success: boolean;
  mode: 'plans_only' | 'full';
  schedulesCount: number;
  summary: Array<{ userId: string; obUserId: string; scope: string; assignmentsObserved: number }>;
}

export function syncObPlans(): Promise<ObCompSyncResult> {
  return invokeFn<ObCompSyncResult>('ob-comp-sync', { mode: 'plans_only' });
}

// ─── ob-comp-inspect-user (admin-only live inspect) ───────────────────────

export type ObInspectStatus = 'missing_plan' | 'needs_review' | 'synced';

export interface ObInspectUpcomingDetail {
  productCategory: string;
  scheduleId: number;
  scheduleName: string;
  effectiveDate: string;
}

export interface ObInspectActiveDetail {
  productCategory: string;
  scheduleId: number;
  effectiveDate: string;
}

export interface ObInspectResult {
  status: ObInspectStatus;
  userId: string;
  // missing_plan
  optimalBlueUserId?: string;
  // needs_review (mixed_or_custom retained as a backward-compat alias for any
  // in-flight client bundles that haven't refreshed; new responses always use
  // 'mixed_core' or 'future_dated_only')
  reason?: 'future_dated_only' | 'mixed_core' | 'mixed_or_custom';
  upcoming?: ObInspectUpcomingDetail[];
  details?: ObInspectActiveDetail[];
  // Scope type stays permissive so legacy cached values still parse, but
  // new derivation only ever produces 'core' / 'mixed' / 'none'.
  scope?: 'core' | 'full_footprint' | 'custom_only' | 'mixed' | 'none';
  // synced
  scheduleId?: number | null;
  scheduleName?: string | null;
  effectiveDate?: string;
}

export function inspectObUser(userId: string): Promise<ObInspectResult> {
  return invokeFn<ObInspectResult>('ob-comp-inspect-user', { userId });
}

// ─── ob-user-link refresh_identity (admin-only OB business-channel/branch repair) ──
// Distinct from inspectObUser (which syncs COMPENSATION assignments): this repairs the
// OB identity — business channel + branch — from the live OB /users record.
export interface ObRefreshIdentityResult {
  status: 'refreshed';
  optimalBlueUserId: string;
  optimalBlueUsername?: string;
  optimalBlueBusinessChannelId?: string | null;
  optimalBlueBranchId?: string | null;
}

export function refreshObIdentity(userId: string): Promise<ObRefreshIdentityResult> {
  return invokeFn<ObRefreshIdentityResult>('ob-user-link', { action: 'refresh_identity', userId });
}

// ─── ob-comp-assign-initial (admin-only first-time setup) ─────────────────

export interface ObAssignInitialAuditFailure {
  productCategory: string;
  expectedScheduleId: number;
  actualScheduleId: number | null;
  actualEffectiveDate: string | null;
}

export interface ObAssignInitialResult {
  status: 'assigned' | 'audit_failed';
  userId: string;
  scheduleId: number;
  scheduleName?: string;
  // After core-only derivation, a successful first-time assign always lands on 'core'
  // ('full_footprint' kept on the type for backward-compat with old responses).
  scope?: 'core' | 'full_footprint';
  effectiveDate?: string;
  failures?: ObAssignInitialAuditFailure[];
}

export function assignInitialObPlan(userId: string, scheduleId: number): Promise<ObAssignInitialResult> {
  return invokeFn<ObAssignInitialResult>('ob-comp-assign-initial', { userId, scheduleId });
}

// ─── ob-comp-repair-missing-core (admin-only 13/14-on-one-schedule fix) ─────

export interface ObRepairMissingCoreFailure {
  productCategory: string;
  expectedScheduleId: number;
  actualScheduleId: number | null;
  actualEffectiveDate: string | null;
}

export interface ObRepairMissingCoreResult {
  status: 'repaired' | 'audit_failed';
  userId: string;
  missingCategory: string;
  scheduleId?: number;
  expectedScheduleId?: number;
  scheduleName?: string;
  scope?: 'core';
  effectiveDate?: string;
  failures?: ObRepairMissingCoreFailure[];
}

export function repairMissingCore(userId: string): Promise<ObRepairMissingCoreResult> {
  return invokeFn<ObRepairMissingCoreResult>('ob-comp-repair-missing-core', { userId });
}
